import pdfMake from 'pdfmake';
import { useEffect, useState } from "react";
import EmailDialog from "./EmailDialog";
import RichTextEditor from "../common/components/RichTextEditor";
import useNetworkEffect from "../common/hooks/networkEffectHook";
import { cdate, now } from "../common/utils/CDate";
import { ReactNodeDialog } from "../common/utils/dialogUtlities";
import { getJobImageData, useJobImages } from "../reducers/jobImageSlice";
import { useJobUpdates } from "../reducers/jobUpdateSlice";
import { JobPrinterFactory } from "../utils/jobPrinting";
import { htmlToText, processTemplate } from "../common/utils/templateUtilities";
import PropTypes from 'prop-types';
import { useSite } from "../reducers/siteSlice";
import { useType } from "../reducers/typeSlice";
import { useLevel } from "../reducers/levelSlice";
import { realizeJobs, useJob } from "../reducers/jobSlice";
import { useProblem } from "../reducers/problemSlice";
import { usePriorities } from "../reducers/prioritySlice";
import { useJobTags } from "../reducers/jobTagSlice";
import { useJobRelatedDocs } from "../reducers/jobRelatedDocSlice";
import { useJobForms } from "../reducers/jobCustomFormSlice";
import { createNote, useJobNotes } from "../reducers/jobNoteSlice";
import { useGlossary } from '../common/hooks/glossaryHook';
import { useWeather } from "../reducers/weatherSlice";
import { useRelatedJobs } from "../reducers/jobRelationSlice";
import useCredentials from "../common/hooks/credentialsHook";
import useNetwork from '../common/hooks/networkHook';
import { useCurrentUser } from '../reducers/userSlice';
import { useRealizationContext } from '../common/reducers/utilities';
import { deepClone } from "../common/utils/objectUtilities";
import { useSpecializedTranslation } from '../hooks/specializedTranslationHook';

const JobUpdateOperation = ({config, update, operation, onCancel, onEmailSent, onEmailError, reason }) => {
    const credentials = useCredentials();
    const realizationContext = useRealizationContext();
    const network = useNetwork();
    const jobUpdates = useJobUpdates(update?.jobid);
    const user = useCurrentUser();
    const [displayUpdate, setDisplayUpdate] = useState();
    const [message, setMessage] = useState();
    
    const job = useJob(update?.jobid);
    const { t, i18n } = useSpecializedTranslation(job);
    const site = useSite(job?.site);
    const level = useLevel(job?.site, job?.level);
    const type = useType(job?.site, job?.level, job?.type);
    const problem = useProblem(job?.site, job?.level, job?.type, job?.problem);
    const priorities = usePriorities();
    const tags = useJobTags();
    const related = useRelatedJobs(update?.jobid);
    const weather = useWeather(job?.created);
    const forms = useJobForms(update?.jobid);
    const images = useJobImages(update?.jobid);
    const notes = useJobNotes(update?.jobid);
    const docs = useJobRelatedDocs(update?.jobid);
    const glossary = useGlossary();

    useNetworkEffect(network => {
        const buildDisplayUpdate = async () => {
            let data = deepClone(update);
            let promises = [];
            if (config?.jobTransform) {
                let operator = eval(config.jobTransform)
                let result = operator(data.job, () => {
                    let canvas = document.createElement('canvas')
                    canvas.style.display = 'none';
                    document.body.appendChild(canvas);
                    return canvas;
                }, () => new Image(), (canvas) => document.body.removeChild(canvas));
                if (result instanceof Promise) {
                    promises.push(result);
                }
            }
            if (data.jobimages?.length > 0) {
                promises = promises.concat(data.jobimages.map(async img => {
                    img.data = await getJobImageData(network, img.jobid, img.filename, 300);
                }));
            }
            await Promise.allSettled(promises);
            setDisplayUpdate(data);
        }
        if (operation && update) {
            buildDisplayUpdate();
        } else {
            setDisplayUpdate();
        }
    }, [update, config?.jobTransform, operation]);

    useEffect(() => {
        let superceeded = false;
        (async () => {
            if (operation && config?.template && displayUpdate) {
                const prev = jobUpdates.filter(u => u.kindid == displayUpdate.kindid && u.updatenumber < displayUpdate.updatenumber).map(prev => ({ ...prev, timestamp_str: cdate(prev.timestamp).dateTimeFormat() }));
                await Promise.allSettled(prev.flatMap(p => {
                    p.jobimages = p.jobimages.map(img => ({ ...img }));
                    return p.jobimages.map(async img => {
                        if (!img.data) {
                            img.data = displayUpdate.jobimages.find(ji => ji.id === img.id)?.data;
                            if (!img.data) {
                                img.data = await getJobImageData(network, img.jobid, img.filename, 300);
                            }
                        }
                    });
                }));
                prev.sort((p1, p2) => {
                    if (p1.updatenumber < p2.updatenumber) {
                        return 1;
                    } else if (p1.updatenumber > p2.updatenumber) {
                        return -1;
                    }
                    return 0;
                })
                const prev_reverse = [...prev];
                prev_reverse.reverse()
                const current = { timestamp: now().toISOString(), ...displayUpdate };
                current.timestamp_str = cdate(current.timestamp).dateTimeFormat();
                prev_reverse.current_timestamp_str = current.timestamp_str;
                const context = {
                    current,
                    prev,
                    prev_reverse,
                    user
                };
                if (config?.disposition === 'email') {
                    if (!superceeded) {
                        setMessage({
                            to: config.to,
                            cc: config.cc,
                            bcc: config.bcc,
                            message: processTemplate(config.template, context),
                            subject: processTemplate(config.subject, context)
                        });
                    }
                } else if (config?.disposition === 'print') {
                    const jobPrinter = JobPrinterFactory(realizationContext, i18n.resolvedLanguage);
                    const pdf = pdfMake.createPdf(await jobPrinter.makeLog(job, site, level, type, problem, priorities, tags, await realizeJobs(realizationContext, related.map(r => r.relatedid)), related, docs, forms, images, notes, weather, glossary, credentials.username, prev), null, { 
                        Roboto: {
                            normal: 'dejavusans.ttf',
                            bold: 'dejavusans-bold.ttf',
                            italics: 'dejavusans-oblique.ttf',
                            bolditalics: 'dejavusans-oblique.ttf'
                        }
                    });
                    pdf.getBlob(blob => {
                        if (!superceeded) {
                            network.downloadBlob(blob, t('jobUpdate').replace(' ', '') + '_' + displayUpdate.jobid + '_' + displayUpdate.updatenumber + '.pdf');
                        }
                    })
                } else {
                    throw new Error('Unknown update disposition ' + config?.disposition)
                }
            } else if (!superceeded) {
                setMessage()
            }
        })();
        return () => { superceeded = true }
    }, [displayUpdate, jobUpdates, config, operation, i18n, t, job, site, level, type, problem, priorities, tags, related, docs, forms, images, notes, weather, glossary, credentials.username, user, network, credentials, realizationContext]);

    if (!operation) {
        return;
    } else if (!message) {
        return <ReactNodeDialog showModal={true} title={<h4>{t('generating')}...</h4>}>{t('generateWaiting')}</ReactNodeDialog>;
    } else if (operation === 'preview') {
        return <ReactNodeDialog title={t('updatePreview')} size="xlg" showModal={true} onClose={onCancel}>
            {config && displayUpdate ? <RichTextEditor value={message?.message} disabled={true}  placeholder={t('preview')}></RichTextEditor> : null}
        </ReactNodeDialog>
    } else if (operation === 'email') {
        return <EmailDialog showModal={true} message={message} readonly={true} onClose={onCancel} onSend={message => { 
            return network.postData('/api/v1/sendmail', JSON.stringify({ to: message.to, cc: message.cc, bcc: message.bcc, subject: message.subject, html: message.message, text: htmlToText(message.message) })).then(data => {
                onEmailSent(data);
                let emails = '';
                if (message.to) {
                    emails += message.to
                }
                if (message.cc) {
                    emails += message.cc;
                }
                if (message.bcc) {
                    emails += message.bcc;
                }
                createNote(network, { username: credentials.username, jobid: update?.jobid, note: config.title + ' #' + displayUpdate?.updatenumber + ' sent to ' + emails + ' for reason: ' + reason })
            }, err => { onEmailError(err) })
        }}></EmailDialog>
    }
}

JobUpdateOperation.propTypes = {
    config: PropTypes.object,
    update: PropTypes.object,
    operation: PropTypes.oneOf([ 'email', 'preview' ]),
    onCancel: PropTypes.func,
    onEmailSent: PropTypes.func
}

export default JobUpdateOperation;